import { motion } from "framer-motion";
import { Pencil, Tag, Megaphone, MessageSquareHeart, Sparkles } from "lucide-react";

const CATEGORIES = [
  {
    id: "graphic",
    n: "01",
    icon: Pencil,
    title: "Graphic Design",
    subtitle: "Eye-catching designs that get noticed.",
    items: [
      ["Social Media Post (Single)", "$75"],
      ["Story Design (Single)", "$50"],
      ["Flyer (8.5×11)", "$125"],
      ["Poster (11×17)", "$150"],
      ["Restaurant Menu (Single Sided)", "$250"],
      ["Restaurant Menu (Double Sided)", "$350"],
      ["Brochure (Tri-Fold)", "$350"],
      ["Business Card Design", "$125"],
      ["Banner / Signage Design", "$175+"],
    ],
    footer: "Need something else? Just ask.",
  },
  {
    id: "branding",
    n: "02",
    icon: Tag,
    title: "Branding",
    subtitle: "Build a brand that leaves a lasting impression.",
    items: [
      ["Logo Design", "$350"],
      ["Brand Identity Package", "$650", "Logo, colours, typography, brand elements"],
      ["Brand Refresh (Rebrand)", "$500", "Update your existing logo and identity"],
    ],
    footer: "Strong brands. Loyal customers.",
  },
  {
    id: "campaigns",
    n: "03",
    icon: Megaphone,
    title: "Marketing Campaigns",
    subtitle: "Strategic campaigns that drive results.",
    items: [
      ["Grand Opening Campaign", "$500", "Pre-launch, launch day, follow-up content"],
      ["Event Promotion Package", "$500", "Custom graphics + promotional materials"],
      ["Seasonal Campaign", "$650", "Holiday, seasonal, or limited-time promotions"],
      ["Fundraiser / Charity Package", "$550", "Awareness campaigns that make an impact"],
    ],
    footer: "We'll handle the strategy and the creativity.",
  },
  {
    id: "social",
    n: "04",
    icon: MessageSquareHeart,
    title: "Social Media Management",
    subtitle: "Consistent content. Real engagement. Growth.",
    items: [
      ["Social Media Launch (One-Time Setup)", "$495", "Profile setup, branded templates, content strategy"],
      ["12 Posts + Stories / Month", "$695/mo"],
      ["16 Posts + Stories / Month", "$895/mo"],
      ["20 Posts + Stories / Month", "$1,095/mo"],
      ["Extra Reel", "$75 each"],
    ],
    footer: "So you can focus on running your business.",
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-40 bg-[#FBF9F6]"
    >
      <div className="max-w-7xl mx-auto w-full px-6 md:px-12">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-3">
            <span className="w-8 h-px bg-[#1C1B1A]"></span>
            <span className="font-mono-label text-[#635F59]">
              Services &amp; Pricing
            </span>
            <span className="w-8 h-px bg-[#1C1B1A]"></span>
          </div>
          <h2 className="mt-8 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Our services,
            <br />
            <span className="italic text-[#B4A087]">your investment.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#635F59] leading-relaxed">
            Transparent pricing for high-impact marketing, design, and strategy.
            <span className="block mt-1">Quality work. Fair prices. Real results.</span>
          </p>
        </div>

        {/* Category grid */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {CATEGORIES.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <motion.article
                key={cat.id}
                data-testid={`pricing-card-${cat.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08 }}
                className="bg-[#FBF9F6] border border-[#E5E0D8] p-8 md:p-10 flex flex-col"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#F2EEE9] border border-[#E5E0D8] flex items-center justify-center shrink-0">
                    <Icon size={20} strokeWidth={1.25} className="text-[#1C1B1A]" />
                  </div>
                  <div>
                    <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">
                      {cat.title}
                    </h3>
                    <p className="font-mono-label text-[#635F59] mt-2">
                      {cat.subtitle}
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between font-mono-label text-[#635F59] pb-3 border-b border-[#E5E0D8]">
                  <span>Service</span>
                  <span>Starting Price</span>
                </div>

                <ul className="divide-y divide-[#E5E0D8]">
                  {cat.items.map(([name, price, sub]) => (
                    <li
                      key={name}
                      className="py-4 flex items-start justify-between gap-6"
                    >
                      <div className="min-w-0">
                        <p className="text-[#1C1B1A]">{name}</p>
                        {sub && (
                          <p className="text-xs text-[#635F59] mt-1 leading-relaxed">
                            {sub}
                          </p>
                        )}
                      </div>
                      <p className="font-serif-display text-lg md:text-xl text-[#1C1B1A] whitespace-nowrap">
                        {price}
                      </p>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-4 border-t border-dashed border-[#E5E0D8] flex items-center gap-2">
                  <Sparkles size={14} className="text-[#B4A087]" strokeWidth={1.5} />
                  <p className="italic text-sm text-[#635F59]">{cat.footer}</p>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Custom + checklist row */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="md:col-span-2 border border-[#E5E0D8] bg-[#F2EEE9] p-8 md:p-10 flex items-center gap-4">
            <Sparkles size={22} className="text-[#B4A087] shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-serif-display italic text-xl md:text-2xl text-[#1C1B1A]">
                Looking for something not listed?
              </p>
              <p className="mt-1 text-sm md:text-base text-[#635F59]">
                Custom proposals are always available. Let&apos;s create a
                package that&apos;s the perfect fit for your business.
              </p>
            </div>
          </div>
          <ul className="border border-[#E5E0D8] p-8 md:p-10 space-y-3">
            {["Custom Solutions", "Flexible Packages", "Personalized Service"].map((f) => (
              <li key={f} className="flex items-center gap-3 text-[#1C1B1A]">
                <span className="w-4 h-4 border border-[#B4A087] rounded-full flex items-center justify-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B4A087]"></span>
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Early client pricing highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          data-testid="pricing-early-client-block"
          className="mt-6 relative overflow-hidden bg-[#1C1B1A] text-[#FBF9F6] p-8 md:p-14 grid grid-cols-12 gap-8 items-center"
        >
          <div className="col-span-12 md:col-span-4 flex md:justify-center">
            <div className="w-40 h-40 md:w-48 md:h-48 rounded-full border border-[#B4A087] flex items-center justify-center text-center">
              <div>
                <p className="font-mono-label text-[#B4A087]">Limited</p>
                <p className="font-serif-display text-2xl md:text-3xl italic mt-2 leading-tight">
                  Early
                  <br />
                  Client
                  <br />
                  Pricing
                </p>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <p className="font-mono-label text-[#B4A087]">
              Exclusive Introductory Pricing · For a Limited Time
            </p>
            <p className="mt-4 font-serif-display text-2xl md:text-3xl leading-snug">
              As Whitney Jean &amp; Co. continues to grow, we&apos;re offering
              exclusive introductory pricing to a limited number of early
              clients.
            </p>
            <p className="mt-4 text-[#FBF9F6]/75 leading-relaxed max-w-2xl">
              This is your opportunity to receive premium design and marketing
              services at rates that will increase as our portfolio and client
              base expand.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                data-testid="pricing-book-consult-cta"
                className="inline-flex items-center justify-center gap-3 bg-[#FBF9F6] text-[#1C1B1A] px-7 py-4 text-sm hover:bg-[#B4A087] hover:text-[#1C1B1A] transition-colors"
              >
                Book a free consultation
                <span aria-hidden>→</span>
              </a>
              <a
                href="#contact"
                data-testid="pricing-custom-quote-cta"
                className="inline-flex items-center justify-center gap-2 border border-[#FBF9F6]/60 px-7 py-4 text-sm hover:border-[#FBF9F6] transition-colors"
              >
                Request a custom quote
              </a>
            </div>
          </div>
        </motion.div>

        <p className="mt-10 text-center font-mono-label text-[#635F59]">
          Thoughtful Strategy · Beautiful Design · Real Results
        </p>
      </div>
    </section>
  );
}
